import React, { useState } from 'react';
import { X, Calendar as CalendarIcon, Clock, Users, Mail, CheckCircle2, Sparkles, User } from 'lucide-react';
import { TimeSlot } from '../../types/calendar';
import { getHuecosDeOro } from '../../utils/huecosDeOro';
interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialDate?: Date;
  leadName?: string;
  decisorEmail?: string;
}
export function BookingModal({
  isOpen,
  onClose,
  initialDate = new Date(),
  leadName = 'Restaurante',
  decisorEmail = ''
}: BookingModalProps) {
  const [step, setStep] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [duration, setDuration] = useState(30);
  const [sendFromSetter, setSendFromSetter] = useState(true);
  const [email, setEmail] = useState(decisorEmail);
  const [subject, setSubject] = useState(`[Growthly] Demo — ${leadName}`);
  const [message, setMessage] = useState('Hola,\n\nTe envío la invitación para nuestra reunión donde te mostraré la propuesta visual.\n\nSaludos.');
  const huecos = getHuecosDeOro(initialDate, duration);
  if (!isOpen) return null;
  const handleNext = () => setStep(2);
  const handleBack = () => setStep(1);
  const handleConfirm = () => setStep(3);
  const formatSlot = (slot: TimeSlot) => {
    const start = new Date(slot.start);
    const end = new Date(slot.end);
    return `${start.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    })} • ${start.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    })} - ${end.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-114">
      <div className="bg-growthly-surface border border-white/10 rounded-xl shadow-2xl max-w-[560px] w-full overflow-hidden flex flex-col max-h-[90vh]" data-id="element-115">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-growthly-panel" data-id="element-116">
          <h2 className="text-[18px] font-semibold text-growthly-text" data-id="element-117">
            {step === 1 ? 'Agendar reunión' : step === 2 ? 'Detalles de la invitación' : '¡Reunión agendada!'}
          </h2>
          {step !== 3 && <button onClick={onClose} className="text-growthly-muted hover:text-growthly-text transition-colors" data-id="element-118">
              <X className="w-5 h-5" data-id="element-119" />
            </button>}
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar" data-id="element-120">
          {/* STEP 1: Slot Selection */}
          {step === 1 && <div className="space-y-6" data-id="element-121">
              <div data-id="element-122">
                <div className="flex items-center justify-between mb-3" data-id="element-123">
                  <label className="text-[13px] font-medium text-growthly-text flex items-center" data-id="element-124">
                    <Sparkles className="w-4 h-4 text-growthly-amber mr-2" data-id="element-125" />
                    Huecos de Oro sugeridos
                  </label>
                  <span className="text-[11px] text-growthly-muted" data-id="element-126">
                    Europe/Madrid
                  </span>
                </div>
                <div className="space-y-2" data-id="element-127">
                  {huecos.map((slot, idx) => <div key={idx} onClick={() => setSelectedSlot(slot)} className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center justify-between ${selectedSlot?.start === slot.start ? 'bg-growthly-amber/10 border-growthly-amber text-growthly-amber' : 'bg-growthly-bg border-white/5 text-growthly-text hover:border-white/20'}`} data-id="element-128">
                      <div className="flex items-center" data-id="element-129">
                        <CalendarIcon className="w-4 h-4 mr-3 opacity-70" data-id="element-130" />
                        <span className="text-[13px] font-medium" data-id="element-131">
                          {formatSlot(slot)}
                        </span>
                      </div>
                      {idx === 0 && <span className="text-[10px] bg-growthly-amber/20 text-growthly-amber px-2 py-0.5 rounded" data-id="element-132">
                          ⭐ Recomendado
                        </span>}
                    </div>)}
                </div>
              </div>

              <div data-id="element-133">
                <label className="text-[13px] font-medium text-growthly-text mb-3 block" data-id="element-134">
                  Duración
                </label>
                <div className="flex space-x-2" data-id="element-135">
                  {[15, 30, 45, 60].map(mins => <button key={mins} onClick={() => setDuration(mins)} className={`px-4 py-1.5 rounded-md text-[12px] font-medium transition-colors ${duration === mins ? 'bg-white/10 text-growthly-text border border-white/20' : 'bg-growthly-bg text-growthly-muted border border-white/5 hover:bg-white/5'}`} data-id="element-136">
                      {mins} min
                    </button>)}
                </div>
              </div>
            </div>}

          {/* STEP 2: Attendees & Message */}
          {step === 2 && <div className="space-y-5" data-id="element-137">
              <div className="bg-growthly-bg border border-white/5 rounded-lg p-4 space-y-4" data-id="element-138">
                <div data-id="element-139">
                  <label className="text-[12px] font-medium text-growthly-muted mb-2 block flex items-center" data-id="element-140">
                    <Users className="w-3 h-3 mr-1.5" data-id="element-141" /> Asistentes
                  </label>
                  <div className="flex items-center space-x-2 mb-2" data-id="element-142">
                    <div className="w-6 h-6 rounded-full bg-growthly-panel flex items-center justify-center" data-id="element-143">
                      <User className="w-3 h-3 text-growthly-muted" data-id="element-144" />
                    </div>
                    <span className="text-[13px] text-growthly-text" data-id="element-145">
                      carlos@growthly.com (Organizador)
                    </span>
                  </div>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email del decisor..." className="input-field mt-2" data-id="element-146" />
                </div>
              </div>

              <div data-id="element-147">
                <label className="text-[12px] font-medium text-growthly-muted mb-1 block" data-id="element-148">
                  Asunto
                </label>
                <input type="text" value={subject} onChange={e => setSubject(e.target.value)} className="input-field" data-id="element-149" />
              </div>

              <div data-id="element-150">
                <label className="text-[12px] font-medium text-growthly-muted mb-1 block" data-id="element-151">
                  Mensaje (.ics description)
                </label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} className="input-field resize-none" data-id="element-152" />
              </div>

              <div className="flex items-start bg-growthly-panel p-3 rounded-lg border border-white/5" data-id="element-153">
                <input type="checkbox" id="sendFromSetter" checked={sendFromSetter} onChange={e => setSendFromSetter(e.target.checked)} className="mt-1 rounded border-white/20 bg-growthly-bg text-growthly-amber focus:ring-growthly-amber" data-id="element-154" />
                <label htmlFor="sendFromSetter" className="ml-3 cursor-pointer" data-id="element-155">
                  <span className="block text-[13px] font-medium text-growthly-text flex items-center" data-id="element-156">
                    <Mail className="w-3 h-3 mr-1.5" data-id="element-157" />
                    Enviar desde el correo del setter
                  </span>
                  <span className="block text-[11px] text-growthly-muted mt-0.5" data-id="element-158">
                    Requiere OAuth conectado. Si se desmarca, se enviará desde
                    el servidor central.
                  </span>
                </label>
              </div>
            </div>}

          {/* STEP 3: Confirmation */}
          {step === 3 && <div className="text-center py-6" data-id="element-159">
              <div className="w-16 h-16 bg-growthly-green/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-160">
                <CheckCircle2 className="w-8 h-8 text-growthly-green" data-id="element-161" />
              </div>
              <h3 className="text-[20px] font-semibold text-growthly-text mb-2" data-id="element-162">
                Reunión agendada y .ics enviado
              </h3>
              <p className="text-[13px] text-growthly-muted mb-6" data-id="element-163">
                Email enviado a {email || 'decisor@restaurante.com'} (simulado)
              </p>

              <div className="bg-growthly-bg border border-white/5 rounded-lg p-4 text-left space-y-3 mb-6" data-id="element-164">
                <div className="flex items-center text-[13px] text-growthly-text" data-id="element-165">
                  <CalendarIcon className="w-4 h-4 mr-3 text-growthly-muted" data-id="element-166" />
                  {selectedSlot ? formatSlot(selectedSlot) : ''}
                </div>
                <div className="flex items-center text-[13px] text-growthly-text" data-id="element-167">
                  <Users className="w-4 h-4 mr-3 text-growthly-muted" data-id="element-168" />
                  carlos@growthly.com, {email || 'decisor@restaurante.com'}
                </div>
              </div>

              <div className="bg-black/50 rounded-lg p-3 text-left overflow-x-auto" data-id="element-169">
                <pre className="text-[10px] text-growthly-muted font-mono" data-id="element-170">
                  {`BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Growthly//CRM//EN
BEGIN:VEVENT
UID:lead-123-evt-abc
DTSTART:${selectedSlot ? new Date(selectedSlot.start).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z' : ''}
DTEND:${selectedSlot ? new Date(selectedSlot.end).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z' : ''}
SUMMARY:${subject}
ORGANIZER;CN=Carlos:mailto:carlos@growthly.com
ATTENDEE;RSVP=TRUE:mailto:${email}
END:VEVENT
END:VCALENDAR`}
                </pre>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/5 bg-growthly-panel flex justify-end space-x-3" data-id="element-171">
          {step === 1 && <>
              <button onClick={onClose} className="btn-secondary" data-id="element-172">
                Cancelar
              </button>
              <button onClick={handleNext} disabled={!selectedSlot} className="btn-primary" data-id="element-173">
                Siguiente →
              </button>
            </>}
          {step === 2 && <>
              <button onClick={handleBack} className="btn-secondary" data-id="element-174">
                ← Atrás
              </button>
              <button onClick={handleConfirm} className="btn-primary bg-growthly-green text-white hover:bg-growthly-green/90" data-id="element-175">
                Confirmar y enviar
              </button>
            </>}
          {step === 3 && <>
              <button onClick={onClose} className="btn-secondary" data-id="element-176">
                Cerrar
              </button>
              <button onClick={onClose} className="btn-primary" data-id="element-177">
                Ver en calendario
              </button>
            </>}
        </div>
      </div>
    </div>;
}